/**
 * SEO & Meta Tag Management for KvikSign SPA
 * Dynamically updates document title, description, Open Graph, and Canonical URLs per route.
 */

import { getIndustryBySlug } from '../data/industriesData';
import { getIntegrationBySlug } from '../data/integrationsData';
import { getArticleBySlug } from '../data/articlesData';
import { ENGLISH_ARTICLES } from '../data/articlesEnglishData';
import { SWEDISH_ARTICLES } from '../data/articlesSwedishData';

export interface PageMetadata {
  title: string;
  description: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
}

const INTEGRATION_OG_MAP: Record<string, string> = {
  'slack': 'https://kviksign.com/images/og/og-slack.png',
  'google-drive': 'https://kviksign.com/images/og/og-google-drive.png',
  'sftp': 'https://kviksign.com/images/og/og-sftp.png',
  'webhooks': 'https://kviksign.com/images/og/og-webhooks.png'
};

const DEFAULT_METADATA: PageMetadata = {
  title: 'KvikSign | Digital Signatur med MitID - Prøv gratis.',
  description: 'Underskriv dokumenter med MitID på 30 sekunder. Juridisk bindende i hele EU. Ingen binding eller dyre abonnementer – start gratis i dag.',
  canonicalPath: '/',
  ogType: 'website',
  ogImage: 'https://kviksign.com/images/og/og-kviksign.png',
};

const ROUTE_METADATA_MAP: Record<string, PageMetadata> = {
  '/': {
    title: 'KvikSign | Digital Signatur med MitID - Prøv gratis.',
    description: 'Underskriv dokumenter med MitID på 30 sekunder. Juridisk bindende i hele EU. Ingen binding eller dyre abonnementer – start gratis i dag.',
    canonicalPath: '/da',
  },
  '/da': {
    title: 'KvikSign | Digital Signatur med MitID - Prøv gratis.',
    description: 'Underskriv dokumenter med MitID på 30 sekunder. Juridisk bindende i hele EU. Ingen binding eller dyre abonnementer – start gratis i dag.',
    canonicalPath: '/da',
  },
  '/en': {
    title: 'KvikSign | Digital Signatures with MitID & eID - Try Free',
    description: 'Sign documents with MitID in 30 seconds. Legally binding across the EU. No lock-in contracts or expensive subscriptions – start free today.',
    canonicalPath: '/en',
  },
  '/sv': {
    title: 'KvikSign | Digital Signatur med BankID & eID - Prova gratis',
    description: 'Signera dokument med BankID på 30 sekunder. Juridiskt bindande i hela EU. Ingen bindningstid eller dyra abonnemang – starta gratis idag.',
    canonicalPath: '/sv',
  },
  '/features': {
    title: 'Funktioner & Moduler | KvikSign Digital Signering',
    description: 'Oplev KvikSigns fire kernefunktioner: KvikSign digital underskrift, KvikID identitetsverifikation, KvikSend fortrolig fildeling og KvikForm formularer.',
    canonicalPath: '/da/features',
  },
  '/funktioner': {
    title: 'Funktioner & Moduler | KvikSign Digital Signering',
    description: 'Oplev KvikSigns fire kernefunktioner: KvikSign digital underskrift, KvikID identitetsverifikation, KvikSend fortrolig fildeling og KvikForm formularer.',
    canonicalPath: '/da/features',
  },
  '/kviksign': {
    title: 'KvikSign – Digital Underskrift med MitID & eIDAS | KvikSign',
    description: 'Få underskrevet kontrakter og aftaler digitalt med MitID, MitID Erhverv og Touchskærm. Hurtig afsendelse og automatisk påmindelsesflow.',
    canonicalPath: '/da/features/kviksign',
  },
  '/features/kviksign': {
    title: 'KvikSign – Digital Underskrift med MitID & eIDAS | KvikSign',
    description: 'Få underskrevet kontrakter og aftaler digitalt med MitID, MitID Erhverv og Touchskærm. Hurtig afsendelse og automatisk påmindelsesflow.',
    canonicalPath: '/da/features/kviksign',
  },
  '/funktioner/kviksign': {
    title: 'KvikSign – Digital Underskrift med MitID & eIDAS | KvikSign',
    description: 'Få underskrevet kontrakter og aftaler digitalt med MitID, MitID Erhverv og Touchskærm. Hurtig afsendelse og automatisk påmindelsesflow.',
    canonicalPath: '/da/features/kviksign',
  },
  '/kviksend': {
    title: 'KvikSend – Sikker og Fortrolig Filoverførsel | KvikSign',
    description: 'Del følsomme dokumenter og filer krypteret og sikkert. Kræv MitID-verifikation eller adgangskode før download.',
    canonicalPath: '/da/features/kviksend',
  },
  '/features/kviksend': {
    title: 'KvikSend – Sikker og Fortrolig Filoverførsel | KvikSign',
    description: 'Del følsomme dokumenter og filer krypteret og sikkert. Kræv MitID-verifikation eller adgangskode før download.',
    canonicalPath: '/da/features/kviksend',
  },
  '/funktioner/kviksend': {
    title: 'KvikSend – Sikker og Fortrolig Filoverførsel | KvikSign',
    description: 'Del følsomme dokumenter og filer krypteret og sikkert. Kræv MitID-verifikation eller adgangskode før download.',
    canonicalPath: '/da/features/kviksend',
  },
  '/kvikid': {
    title: 'KvikID – Digital Identitetsbekræftelse med MitID | KvikSign',
    description: 'Verificer dine kunders identitet og CPR-match hurtigt og sikkert med officiel MitID integration. Overhold hvidvask- og KYC-krav.',
    canonicalPath: '/da/features/kvikid',
  },
  '/features/kvikid': {
    title: 'KvikID – Digital Identitetsbekræftelse med MitID | KvikSign',
    description: 'Verificer dine kunders identitet og CPR-match hurtigt og sikkert med officiel MitID integration. Overhold hvidvask- og KYC-krav.',
    canonicalPath: '/da/features/kvikid',
  },
  '/funktioner/kvikid': {
    title: 'KvikID – Digital Identitetsbekræftelse med MitID | KvikSign',
    description: 'Verificer dine kunders identitet og CPR-match hurtigt og sikkert med officiel MitID integration. Overhold hvidvask- og KYC-krav.',
    canonicalPath: '/da/features/kvikid',
  },
  '/kvikform': {
    title: 'KvikForm – Interaktive Formularer & Skabeloner | KvikSign',
    description: 'Opret intuitive digitale formularer og skabeloner. Indsaml data, bilag og underskrifter i ét samlet, automatiseret arbejdsflow.',
    canonicalPath: '/da/features/kvikform',
  },
  '/features/kvikform': {
    title: 'KvikForm – Interaktive Formularer & Skabeloner | KvikSign',
    description: 'Opret intuitive digitale formularer og skabeloner. Indsaml data, bilag og underskrifter i ét samlet, automatiseret arbejdsflow.',
    canonicalPath: '/da/features/kvikform',
  },
  '/funktioner/kvikform': {
    title: 'KvikForm – Interaktive Formularer & Skabeloner | KvikSign',
    description: 'Opret intuitive digitale formularer og skabeloner. Indsaml data, bilag og underskrifter i ét samlet, automatiseret arbejdsflow.',
    canonicalPath: '/da/features/kvikform',
  },
  '/pricing': {
    title: 'Priser & Gennemskuelige Abonnementer | KvikSign',
    description: 'Se KvikSigns priser og abonnementer. Start med en gratis prøveperiode med 5 gratis credits uden binding.',
    canonicalPath: '/da/pricing',
  },
  '/priser': {
    title: 'Priser & Gennemskuelige Abonnementer | KvikSign',
    description: 'Se KvikSigns priser og abonnementer. Start med en gratis prøveperiode med 5 gratis credits uden binding.',
    canonicalPath: '/da/pricing',
  },
  '/contact': {
    title: 'Kontakt Os & Dansk Support | KvikSign',
    description: 'Har du spørgsmål til KvikSign platformen, brug for rådgivning eller et skræddersyet tilbud? Kontakt vores danske supportteam.',
    canonicalPath: '/da/contact',
  },
  '/kontakt-os': {
    title: 'Kontakt Os & Dansk Support | KvikSign',
    description: 'Har du spørgsmål til KvikSign platformen, brug for rådgivning eller et skræddersyet tilbud? Kontakt vores danske supportteam.',
    canonicalPath: '/da/contact',
  },
  '/kontakt': {
    title: 'Kontakt Os & Dansk Support | KvikSign',
    description: 'Har du spørgsmål til KvikSign platformen, brug for rådgivning eller et skræddersyet tilbud? Kontakt vores danske supportteam.',
    canonicalPath: '/da/contact',
  },
  '/terms': {
    title: 'Forretningsbetingelser & Databehandleraftale | KvikSign',
    description: 'Læs KvikSigns generelle forretningsbetingelser, vilkår for brug og databehandleraftale (DPA) i henhold til GDPR og eIDAS.',
    canonicalPath: '/da/terms',
  },
  '/betingelser': {
    title: 'Forretningsbetingelser & Databehandleraftale | KvikSign',
    description: 'Læs KvikSigns generelle forretningsbetingelser, vilkår for brug og databehandleraftale (DPA) i henhold til GDPR og eIDAS.',
    canonicalPath: '/da/terms',
  },
  '/privacy': {
    title: 'Privatlivspolitik & GDPR Databeskyttelse | KvikSign',
    description: 'Læs om hvordan KvikSign behandler og beskytter dine personoplysninger i fuld overensstemmelse med GDPR.',
    canonicalPath: '/da/privacy',
  },
  '/privatlivspolitik': {
    title: 'Privatlivspolitik & GDPR Databeskyttelse | KvikSign',
    description: 'Læs om hvordan KvikSign behandler og beskytter dine personoplysninger i fuld overensstemmelse med GDPR.',
    canonicalPath: '/da/privacy',
  },
  '/security': {
    title: 'Sikkerhed, Drift & Compliance | KvikSign',
    description: 'Læs om KvikSigns sikkerhedsarkitektur, drift, kryptering og compliance.',
    canonicalPath: '/da/security',
  },
  '/sikkerhed-og-drift': {
    title: 'Sikkerhed, Drift & Compliance | KvikSign',
    description: 'Læs om KvikSigns sikkerhedsarkitektur, drift, kryptering og compliance.',
    canonicalPath: '/da/security',
  },
  '/login': {
    title: 'Log ind | KvikSign Digital Signering',
    description: 'Log ind på KvikSign for at tilgå jeres digitale signeringer og aftaler.',
    canonicalPath: '/da/login',
  },
  '/signup': {
    title: 'Opret gratis konto | KvikSign Digital Signering',
    description: 'Opret en gratis KvikSign-konto på under 1 minut. Start med 5 gratis credits og send dokumenter til digital signering.',
    canonicalPath: '/da/signup',
  },
  '/set-password': {
    title: 'Opret Adgangskode | KvikSign',
    description: 'Indstil din personlige adgangskode for at fuldføre oprettelsen af din KvikSign brugerkonto.',
    canonicalPath: '/set-password',
  },
  '/mitid-verify': {
    title: 'MitID Identitetsverifikation | KvikSign',
    description: 'Gennemfør officiel digital identitetsbekræftelse sikkert via MitID.',
    canonicalPath: '/mitid-verify',
  },
  '/brancher': {
    title: 'Skræddersyede digitale løsninger til din branche | KvikSign',
    description: 'Se hvordan KvikSign, KvikID, KvikSend og KvikForm optimerer arbejdsgange, sikrer lovkrav og sparer tid i din branche.',
    canonicalPath: '/da/brancher',
  },
  '/industries': {
    title: 'Skræddersyede digitale løsninger til din branche | KvikSign',
    description: 'Se hvordan KvikSign, KvikID, KvikSend og KvikForm optimerer arbejdsgange, sikrer lovkrav og sparer tid i din branche.',
    canonicalPath: '/da/brancher',
  },
  '/integrationer': {
    title: 'Integrationer & Cloud-tilslutninger | KvikSign Digital Signatur',
    description: 'Forbind KvikSign direkte med Google Drive, Slack, SFTP og Webhooks for automatisk arkivering af underskrevne dokumenter og realtidsvarsler.',
    canonicalPath: '/da/integrationer',
  },
  '/integrations': {
    title: 'Integrationer & Cloud-tilslutninger | KvikSign Digital Signatur',
    description: 'Forbind KvikSign direkte med Google Drive, Slack, SFTP og Webhooks for automatisk arkivering af underskrevne dokumenter og realtidsvarsler.',
    canonicalPath: '/da/integrationer',
  },
  '/articles': {
    title: 'Artikler & Viden om Digital Signering og MitID | KvikSign',
    description: 'Bliv klogere på regler for digital underskrift, MitID vs. MitID Erhverv, juridisk gyldighed, eIDAS, QES og sikker dokumenthåndtering.',
    canonicalPath: '/da/articles',
  },
  '/artikler': {
    title: 'Artikler & Viden om Digital Signering og MitID | KvikSign',
    description: 'Bliv klogere på regler for digital underskrift, MitID vs. MitID Erhverv, juridisk gyldighed, eIDAS, QES og sikker dokumenthåndtering.',
    canonicalPath: '/da/articles',
  },
  '/viden': {
    title: 'Artikler & Viden om Digital Signering og MitID | KvikSign',
    description: 'Bliv klogere på regler for digital underskrift, MitID vs. MitID Erhverv, juridisk gyldighed, eIDAS, QES og sikker dokumenthåndtering.',
    canonicalPath: '/da/articles',
  },
  '/blog': {
    title: 'Artikler & Viden om Digital Signering og MitID | KvikSign',
    description: 'Bliv klogere på regler for digital underskrift, MitID vs. MitID Erhverv, juridisk gyldighed, eIDAS, QES og sikker dokumenthåndtering.',
    canonicalPath: '/da/articles',
  },
  '/app/sign/dashboard': {
    title: 'Kontrolpanel | KvikSign',
    description: 'Oversigt over dine aktive dokumenter, forbrugte credits og seneste hændelser.',
  },
  '/app/sign/transactions': {
    title: 'Forsendelser & Transaktioner | KvikSign',
    description: 'Se alle gennemførte, igangværende og afsluttede dokumentsigneringer og filoverførsler.',
  },
  '/app/sign/send': {
    title: 'Send Nyt Dokument til Signering | KvikSign',
    description: 'Upload dokumenter og send til digital underskrift med MitID, MitID Erhverv eller Touchskærm.',
  },
  '/app/sign/drafts': {
    title: 'Kladder | KvikSign',
    description: 'Dine gemte kladder klar til videre redigering og afsendelse.',
  },
  '/settings': {
    title: 'Indstillinger & Firmaoplysninger | KvikSign',
    description: 'Administrer firmalogo, branding, brugere, abonnement og API-integrationer.',
  },
  '/app/settings': {
    title: 'Indstillinger & Firmaoplysninger | KvikSign',
    description: 'Administrer firmalogo, branding, brugere, abonnement og API-integrationer.',
  },
  '/app/profile': {
    title: 'Min Brugerprofil | KvikSign',
    description: 'Administrer dine personlige brugeroplysninger og sikkerhedsindstillinger.',
  },
  '/app/superadmin': {
    title: 'Superadmin Kontrolpanel | KvikSign',
    description: 'Systemadministration og overblik over KvikSign platformen.',
  },
};

const ENGLISH_ROUTE_METADATA_MAP: Record<string, PageMetadata> = {
  '/': {
    title: 'KvikSign - Legally Binding E-Signatures & Secure File Sharing',
    description: 'Send documents for legally binding digital signing with MitID, touchscreen, and eIDAS compliance in seconds. Simple, fast, and secure Danish platform.',
    canonicalPath: '/en',
  },
  '/features': {
    title: 'Features & Modules | KvikSign Digital Signing',
    description: 'Explore KvikSign core modules: KvikSign digital signatures, KvikID identity verification, KvikSend confidential file sharing, and KvikForm forms.',
    canonicalPath: '/en/features',
  },
  '/funktioner': {
    title: 'Features & Modules | KvikSign Digital Signing',
    description: 'Explore KvikSign core modules: KvikSign digital signatures, KvikID identity verification, KvikSend confidential file sharing, and KvikForm forms.',
    canonicalPath: '/en/features',
  },
  '/kviksign': {
    title: 'KvikSign – Digital Signature with MitID & eIDAS | KvikSign',
    description: 'Get contracts and agreements signed digitally with MitID and touchscreen. Instant dispatch and automated reminder workflows.',
    canonicalPath: '/en/features/kviksign',
  },
  '/funktioner/kviksign': {
    title: 'KvikSign – Digital Signature with MitID & eIDAS | KvikSign',
    description: 'Get contracts and agreements signed digitally with MitID and touchscreen. Instant dispatch and automated reminder workflows.',
    canonicalPath: '/en/features/kviksign',
  },
  '/features/kviksign': {
    title: 'KvikSign – Digital Signature with MitID & eIDAS | KvikSign',
    description: 'Get contracts and agreements signed digitally with MitID and touchscreen. Instant dispatch and automated reminder workflows.',
    canonicalPath: '/en/features/kviksign',
  },
  '/kviksend': {
    title: 'KvikSend – Confidential and Secure File Sharing | KvikSign',
    description: 'Share confidential documents and files end-to-end encrypted. Require MitID verification or password before recipient download.',
    canonicalPath: '/en/features/kviksend',
  },
  '/funktioner/kviksend': {
    title: 'KvikSend – Confidential and Secure File Sharing | KvikSign',
    description: 'Share confidential documents and files end-to-end encrypted. Require MitID verification or password before recipient download.',
    canonicalPath: '/en/features/kviksend',
  },
  '/features/kviksend': {
    title: 'KvikSend – Confidential and Secure File Sharing | KvikSign',
    description: 'Share confidential documents and files end-to-end encrypted. Require MitID verification or password before recipient download.',
    canonicalPath: '/en/features/kviksend',
  },
  '/kvikid': {
    title: 'KvikID – Digital Identity Verification with MitID | KvikSign',
    description: 'Verify customer identities and CPR matches quickly and reliably with official MitID integration. Comply with AML and KYC regulations.',
    canonicalPath: '/en/features/kvikid',
  },
  '/funktioner/kvikid': {
    title: 'KvikID – Digital Identity Verification with MitID | KvikSign',
    description: 'Verify customer identities and CPR matches quickly and reliably with official MitID integration. Comply with AML and KYC regulations.',
    canonicalPath: '/en/features/kvikid',
  },
  '/features/kvikid': {
    title: 'KvikID – Digital Identity Verification with MitID | KvikSign',
    description: 'Verify customer identities and CPR matches quickly and reliably with official MitID integration. Comply with AML and KYC regulations.',
    canonicalPath: '/en/features/kvikid',
  },
  '/kvikform': {
    title: 'KvikForm – Interactive Forms & Document Templates | KvikSign',
    description: 'Build intuitive digital forms and templates. Collect client data, attachments, and signatures in a single automated workflow.',
    canonicalPath: '/en/features/kvikform',
  },
  '/funktioner/kvikform': {
    title: 'KvikForm – Interactive Forms & Document Templates | KvikSign',
    description: 'Build intuitive digital forms and templates. Collect client data, attachments, and signatures in a single automated workflow.',
    canonicalPath: '/en/features/kvikform',
  },
  '/features/kvikform': {
    title: 'KvikForm – Interactive Forms & Document Templates | KvikSign',
    description: 'Build intuitive digital forms and templates. Collect client data, attachments, and signatures in a single automated workflow.',
    canonicalPath: '/en/features/kvikform',
  },
  '/pricing': {
    title: 'Pricing & Transparent Subscriptions | KvikSign',
    description: 'Explore KvikSign pricing and plans. Start with a free trial including 5 free credits and zero binding commitment.',
    canonicalPath: '/en/pricing',
  },
  '/priser': {
    title: 'Pricing & Transparent Subscriptions | KvikSign',
    description: 'Explore KvikSign pricing and plans. Start with a free trial including 5 free credits and zero binding commitment.',
    canonicalPath: '/en/pricing',
  },
  '/contact': {
    title: 'Contact Us & Dedicated Support | KvikSign',
    description: 'Have questions about KvikSign, need custom advisory or an enterprise quote? Contact our dedicated support team.',
    canonicalPath: '/en/contact',
  },
  '/kontakt-os': {
    title: 'Contact Us & Dedicated Support | KvikSign',
    description: 'Have questions about KvikSign, need custom advisory or an enterprise quote? Contact our dedicated support team.',
    canonicalPath: '/en/contact',
  },
  '/terms': {
    title: 'Terms of Service & Data Processing Agreement | KvikSign',
    description: 'Read KvikSign general terms of service, conditions of use, and Data Processing Agreement (DPA) under GDPR and eIDAS.',
    canonicalPath: '/en/terms',
  },
  '/betingelser': {
    title: 'Terms of Service & Data Processing Agreement | KvikSign',
    description: 'Read KvikSign general terms of service, conditions of use, and Data Processing Agreement (DPA) under GDPR and eIDAS.',
    canonicalPath: '/en/terms',
  },
  '/privacy': {
    title: 'Privacy Policy & GDPR Data Protection | KvikSign',
    description: 'Learn how KvikSign processes and protects your personal data in full compliance with European GDPR.',
    canonicalPath: '/en/privacy',
  },
  '/privatlivspolitik': {
    title: 'Privacy Policy & GDPR Data Protection | KvikSign',
    description: 'Learn how KvikSign processes and protects your personal data in full compliance with European GDPR.',
    canonicalPath: '/en/privacy',
  },
  '/security': {
    title: 'Security, Operations & Compliance | KvikSign',
    description: 'Learn about KvikSign infrastructure security, operational stability, encryption standards, and European regulatory compliance.',
    canonicalPath: '/en/security',
  },
  '/industries': {
    title: 'Tailored Digital Solutions for Your Industry | KvikSign',
    description: 'See how KvikSign, KvikID, KvikSend, and KvikForm optimize workflows, ensure legal compliance, and save time across 10 industries.',
    canonicalPath: '/en/industries',
  },
  '/brancher': {
    title: 'Tailored Digital Solutions for Your Industry | KvikSign',
    description: 'See how KvikSign, KvikID, KvikSend, and KvikForm optimize workflows, ensure legal compliance, and save time across 10 industries.',
    canonicalPath: '/en/industries',
  },
  '/integrations': {
    title: 'Integrations & Cloud Connections | KvikSign Digital Signing',
    description: 'Connect KvikSign directly with Google Drive, Slack, SFTP, and Webhooks for automated document archiving, real-time alerts, and seamless workflows.',
    canonicalPath: '/en/integrations',
  },
  '/integrationer': {
    title: 'Integrations & Cloud Connections | KvikSign Digital Signing',
    description: 'Connect KvikSign directly with Google Drive, Slack, SFTP, and Webhooks for automated document archiving, real-time alerts, and seamless workflows.',
    canonicalPath: '/en/integrations',
  },
  '/articles': {
    title: 'Articles & Knowledge on Digital Signing & eIDAS | KvikSign',
    description: 'Explore in-depth guides on digital signatures, MitID vs. Business ID, legal validity, eIDAS compliance, QES, and secure document management.',
    canonicalPath: '/en/articles',
  },
  '/artikler': {
    title: 'Articles & Knowledge on Digital Signing & eIDAS | KvikSign',
    description: 'Explore in-depth guides on digital signatures, MitID vs. Business ID, legal validity, eIDAS compliance, QES, and secure document management.',
    canonicalPath: '/en/articles',
  },
  '/login': {
    title: 'Log In | KvikSign Digital Signing',
    description: 'Log in to KvikSign to manage your digital signatures, forms, and identity verifications.',
    canonicalPath: '/en/login',
  },
  '/signup': {
    title: 'Create Free Account | KvikSign Digital Signing',
    description: 'Create a free KvikSign account in under 1 minute. Get started with 5 free credits and sign documents effortlessly.',
    canonicalPath: '/en/signup',
  },
};

/**
 * Gets page metadata based on current pathname
 */
export function getPageMetadata(pathname: string): PageMetadata {
  const rawPath = pathname.split('?')[0].split('#')[0];
  const isEnglish = rawPath.startsWith('/en/') || rawPath === '/en' || rawPath.startsWith('/eng/') || rawPath === '/eng';
  const isSwedish = rawPath.startsWith('/sv/') || rawPath === '/sv';
  const lang: 'da' | 'en' | 'sv' = isSwedish ? 'sv' : (isEnglish ? 'en' : 'da');
  
  let cleanPath = rawPath;
  if (cleanPath.startsWith('/dk/') || cleanPath.startsWith('/da/')) {
    cleanPath = cleanPath.substring(3);
  } else if (cleanPath === '/dk' || cleanPath === '/da') {
    cleanPath = '/';
  } else if (cleanPath.startsWith('/eng/')) {
    cleanPath = cleanPath.substring(4);
  } else if (cleanPath.startsWith('/en/')) {
    cleanPath = cleanPath.substring(3);
  } else if (cleanPath === '/eng' || cleanPath === '/en') {
    cleanPath = '/';
  } else if (cleanPath.startsWith('/sv/')) {
    cleanPath = cleanPath.substring(3);
  } else if (cleanPath === '/sv') {
    cleanPath = '/';
  }

  if (isEnglish) {
    if (ENGLISH_ROUTE_METADATA_MAP[cleanPath]) {
      return ENGLISH_ROUTE_METADATA_MAP[cleanPath];
    }
  } else {
    if (ROUTE_METADATA_MAP[cleanPath]) {
      return ROUTE_METADATA_MAP[cleanPath];
    }
  }

  // Handle dynamic routes
  if (cleanPath.startsWith('/integrationer/') || cleanPath.startsWith('/integrations/') || cleanPath.startsWith('/integration/')) {
    const slug = cleanPath.replace('/integrations/', '').replace('/integrationer/', '').replace('/integration/', '').replace(/^\//, '');
    const intDetail = getIntegrationBySlug(slug);
    if (intDetail) {
      return {
        title: isEnglish ? intDetail.englishSeoTitle : intDetail.seoTitle,
        description: isEnglish ? intDetail.englishSeoDescription : intDetail.seoDescription,
        canonicalPath: isEnglish ? `/en/integrations/${intDetail.englishSlug || intDetail.slug}` : `/da/integrationer/${intDetail.slug}`,
        ogImage: INTEGRATION_OG_MAP[intDetail.slug] || INTEGRATION_OG_MAP[intDetail.id] || intDetail.heroImage,
      };
    }
  }

  if (cleanPath.startsWith('/brancher/') || cleanPath.startsWith('/industries/') || cleanPath.startsWith('/branche/')) {
    const slug = cleanPath.replace('/industries/', '').replace('/brancher/', '').replace('/branche/', '').replace(/^\//, '');
    const ind = getIndustryBySlug(slug, isEnglish ? 'en' : 'da');
    if (ind) {
      const pageTitle = isEnglish
        ? (ind.englishSeoTitle || `${ind.englishName} | KvikSign`)
        : (ind.seoTitle || `${ind.name} | KvikSign`);
      return {
        title: pageTitle,
        description: isEnglish ? ind.englishMetaDescription : ind.metaDescription,
        canonicalPath: isEnglish ? `/en/industries/${ind.slug}` : `/da/brancher/${ind.slug}`,
        ogImage: ind.heroImage,
      };
    }
  }

  if (cleanPath.startsWith('/articles/') || cleanPath.startsWith('/artikler/') || cleanPath.startsWith('/viden/') || cleanPath.startsWith('/blog/') || cleanPath.startsWith('/artiklar/')) {
    const slug = cleanPath.replace('/articles/', '').replace('/artikler/', '').replace('/viden/', '').replace('/blog/', '').replace('/artiklar/', '').replace(/^\//, '');
    const art = getArticleBySlug(slug, lang);
    if (art) {
      const pageTitle = art.metaTitle || (art.title.length <= 55 ? `${art.title} | KvikSign` : art.title);
      const hasEn = Boolean(ENGLISH_ARTICLES[art.slug]);
      const hasSv = Boolean(SWEDISH_ARTICLES[art.slug]);
      const canonicalPath = isSwedish
        ? (hasSv ? `/sv/articles/${art.slug}` : `/da/articles/${art.slug}`)
        : (isEnglish
          ? (hasEn ? `/en/articles/${art.slug}` : `/da/articles/${art.slug}`)
          : `/da/articles/${art.slug}`);

      return {
        title: pageTitle,
        description: art.metaDescription,
        canonicalPath,
        ogImage: art.heroImage,
      };
    }
  }

  if (cleanPath.startsWith('/f/')) {
    return {
      title: isEnglish ? 'Fill Out Form | KvikSign' : 'Udfyld Formular | KvikSign',
      description: isEnglish ? 'Fill out and sign the requested form securely online via KvikSign.' : 'Udfyld og underskriv den modtagne formular sikkert online via KvikSign.',
      canonicalPath: cleanPath,
    };
  }

  if (cleanPath.startsWith('/sign/')) {
    return {
      title: isEnglish ? 'Sign Document | KvikSign Secure Signing' : 'Underskriv Dokument | KvikSign Sikker Signering',
      description: isEnglish ? 'Review and sign your document securely with MitID or Touchscreen.' : 'Gennemlæs og underskriv dokumentet sikkert med MitID eller Touchskærm.',
      canonicalPath: cleanPath,
    };
  }

  if (cleanPath.startsWith('/app/')) {
    return {
      title: 'Workspace | KvikSign',
      description: 'KvikSign administrationspanel.',
    };
  }

  return isEnglish ? (ENGLISH_ROUTE_METADATA_MAP['/'] || DEFAULT_METADATA) : DEFAULT_METADATA;
}

/**
 * Updates DOM head elements with page metadata and hreflang links
 */
export function applyPageMetadata(metadata: PageMetadata) {
  if (typeof document === 'undefined') return;

  const baseUrl = 'https://kviksign.com';

  // 1. Document Title
  document.title = metadata.title;

  // 2. Helper to set/update meta tag
  const setMetaTag = (selector: string, attrName: string, attrValue: string, content: string) => {
    let el = document.querySelector(selector) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attrName, attrValue);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  // 3. Helper to set/update link tag
  const setLinkTag = (rel: string, href: string, hreflang?: string) => {
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.querySelector(selector) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      if (hreflang) el.setAttribute('hreflang', hreflang);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  // Standard Meta Tags
  setMetaTag('meta[name="title"]', 'name', 'title', metadata.title);
  setMetaTag('meta[name="description"]', 'name', 'description', metadata.description);

  // Open Graph
  setMetaTag('meta[property="og:title"]', 'property', 'og:title', metadata.title);
  setMetaTag('meta[property="og:description"]', 'property', 'og:description', metadata.description);
  
  if (metadata.ogImage) {
    const absImage = metadata.ogImage.startsWith('http')
      ? metadata.ogImage
      : `${baseUrl}${metadata.ogImage.startsWith('/') ? '' : '/'}${metadata.ogImage}`;
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', absImage);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', absImage);
  }
  
  if (metadata.canonicalPath) {
    const fullUrl = `${baseUrl}${metadata.canonicalPath.startsWith('/') ? metadata.canonicalPath : '/' + metadata.canonicalPath}`;
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', fullUrl);
    setMetaTag('meta[name="twitter:url"]', 'name', 'twitter:url', fullUrl);
    setLinkTag('canonical', fullUrl);

    // Hreflang tags
    const cleanPath = metadata.canonicalPath.replace(/^\/(en|eng|da|dk|sv)(\/|$)/, '/');
    const daUrl = `${baseUrl}/da${cleanPath === '/' ? '' : cleanPath}`;
    const enUrl = `${baseUrl}/en${cleanPath === '/' ? '' : cleanPath}`;
    const svUrl = `${baseUrl}/sv${cleanPath === '/' ? '' : cleanPath}`;
    
    setLinkTag('alternate', daUrl, 'da');
    setLinkTag('alternate', enUrl, 'en');
    setLinkTag('alternate', svUrl, 'sv');
    setLinkTag('alternate', daUrl, 'x-default');
  }

  // Twitter
  setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', metadata.title);
  setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', metadata.description);
}
